"use client"

// ds-color-ignore-file — illustrative readout tones + severity accents ported
// 1:1 from the immunity/metabolism immersive readout (report palette), not DS
// surface colours.

/* "What your hormones can actually tell you" — immersive scroll-pinned reveal.
   Component ported 1:1 from the immunity LP (ImmersiveFullPicture), with our
   hormones content + image. Mechanic: an outer full-bleed `track` (height
   TRACK_VH vh) gives the scroll runway; a child `stage` is `sticky top-0
   h-screen` and stays pinned while you scroll through it. A rAF-throttled
   scroll listener computes progress p and a single applyProgress(p) writes CSS
   custom properties on the stage imperatively (no React state / re-render per
   frame). Consumers read the vars with var().

   Blur is a STATIC pre-blurred layer + a sharp layer whose opacity cross-fades
   (never animate filter:blur per frame). No framer-motion. Honours
   prefers-reduced-motion (final state, no listener). After editing
   applyProgress you MUST full-reload to test (HMR won't re-attach the
   listener).

   Readings are illustrative example values (we never diagnose). */

import { useCallback, useEffect, useRef, useState } from "react"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { goKeys as k } from "@/i18n/go-keys"
import { blobMediaUrl } from "@/lib/blob-url"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import {
  REVEAL_INSIGHTS,
  REVEAL_FINDINGS,
  type RevealInsight,
  type RevealTone,
} from "./female-hormones-data"
import { SECTION } from "./female-hormones-components"

const BG_IMAGE = blobMediaUrl(
  "/images/marketing/female-hormones-reveal-office.jpg"
)
const ADVANCE_MS = 7000 // insight auto-advance interval (ms)

// immersive scroll tuning (immunity IMMERSIVE_DEFAULTS)
const TRACK_VH = 240 // scroll runway length
const BLUR_START = 0.04 // photo blur begins (early)
const BLUR_END = 0.2 // …and finishes quickly
const REVEAL_START = 0.2 // content slide begins
const REVEAL_END = 0.9 // …reaches final position
const RISE_RATIO = 1 // 1:1 — block moves the reveal-window's scroll px
const FADE_BAND = 150 // px over which the content fades in, low in view
const MAX_BLUR = 6
const DARKEN = 0.08

/* ── status badge + range bar (1:1 with the immunity card primitives) ── */
function StatusBadge({ tone, label }: { tone: RevealTone; label: string }) {
  const cls = {
    green: "bg-[#e0e6b0] text-[#596625]",
    sand: "bg-[#f2efe8] text-[#7a5c4c]",
    terra: "bg-[#f7e1d8] text-[#b0432d]",
  }[tone]
  return (
    <span
      className={`type-label-md inline-flex items-center rounded-full px-2.5 py-1 font-medium ${cls}`}
    >
      {label}
    </span>
  )
}

const ZONE_COLOR: Record<string, string> = {
  sand: "#b79c80",
  green: "#95a73b",
  terra: "#e96f3f",
}

function ZoneBar({
  zones,
  dot,
}: {
  zones: readonly { c: string; w: number }[]
  dot: number
}) {
  return (
    <div className="relative h-[6px] w-full">
      <div className="flex h-full w-full gap-[3px]">
        {zones.map((z, i) => (
          <div
            key={i}
            className="h-full rounded-full"
            style={{ width: `${z.w}%`, background: ZONE_COLOR[z.c] }}
          />
        ))}
      </div>
      <div
        className="absolute top-1/2 size-[15px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-black bg-white"
        style={{ left: `${dot}%` }}
      />
    </div>
  )
}

/* The MRI read has no blood values, so ZoneBars don't fit. Instead: the three
   structural findings a pelvic MRI flags directly, three-in-a-row — kept
   deliberately minimal (finding name + severity badge only, no illustrative
   glyph, no prose), mirroring the §7b finding chips on the white readout card. */
function StructureReadout() {
  const t = useTranslations("go")
  return (
    <div className="mt-[37px] flex flex-col gap-4">
      <p className="type-label-md text-[#919191]">
        {t(k.femaleHormonesRevealStructureLabel)}
      </p>
      <div className="grid grid-cols-3 gap-3">
        {REVEAL_FINDINGS.map((f) => (
          <div
            key={f.titleKey}
            className="flex flex-col gap-1 rounded-[12px] bg-neutral-100 px-3 py-2.5"
          >
            <span className="type-body-sm font-semibold text-neutral-700">
              {t(f.titleKey)}
            </span>
            <span className="type-label-md inline-flex items-center gap-1.5 text-neutral-500">
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: ZONE_COLOR[f.tone] }}
              />
              {f.count} {t(f.badgeKey)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Readout({ ins }: { ins: RevealInsight }) {
  const t = useTranslations("go")
  return (
    <>
      {/* combined reading — the synthesis verdict + the active insight's body */}
      <div className="flex flex-col gap-2">
        <p className="type-label-md text-[#919191]">
          {t(k.femaleHormonesRevealCombinedLabel)}
        </p>
        <p className="type-headline-md max-w-[464px] text-[#232323]">
          &ldquo;{t(ins.verdictKey)}&rdquo;
        </p>
        <p className="type-body-lg mt-2 max-w-[464px] text-neutral-700">
          {t(ins.bodyKey)}
        </p>
      </div>
      {ins.id === "structure" ? (
        <StructureReadout />
      ) : (
        <div className="mt-[37px] flex flex-col">
          <p className="type-label-md text-[#919191]">
            {t(k.femaleHormonesRevealNormalLabel)}
          </p>
          <div className="mt-2 flex flex-col gap-3">
            {ins.readings.map((r, i) => (
              <div key={i} className="flex flex-col gap-[5px]">
                <div className="flex items-end justify-between gap-4">
                  <div className="flex items-center gap-[14px] py-[7px]">
                    <span className="type-title-lg text-[#2d2d2d]">
                      {t(r.markerKey)}
                    </span>
                    <StatusBadge tone={r.tone} label={t(r.badgeKey)} />
                  </div>
                  <div className="flex items-baseline gap-0.5">
                    <span className="type-display-md text-[#101828]">
                      {r.value}
                    </span>
                    <span className="type-title-md text-[#919191]">
                      {r.unit}
                    </span>
                  </div>
                </div>
                <ZoneBar zones={r.zones} dot={r.dot} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default function HormoneReveal({
  showImaging,
}: {
  /** Blood-only markets drop the `structure` tab (the pelvic-MRI read). */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  // `active` indexes the FILTERED list, and both the auto-advance wrap and the
  // render below are modulo/clamped to it, so dropping a tab can never leave
  // the index out of range or stall auto-play.
  const insights = REVEAL_INSIGHTS.filter(
    (it) => showImaging || it.id !== "structure"
  )
  const [active, setActive] = useState(0)
  const [started, setStarted] = useState(false)
  const [autoplay, setAutoplay] = useState(true)
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const totalRef = useRef(1) // total scroll px (track.offsetHeight - innerHeight)

  // One imperative writer; sets CSS vars on the stage — NO React state per frame.
  const applyProgress = useCallback((p: number) => {
    const stage = stageRef.current
    if (!stage) return
    const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v))
    const t = clamp((p - BLUR_START) / Math.max(0.01, BLUR_END - BLUR_START))
    const rl = clamp(
      (p - REVEAL_START) / Math.max(0.01, REVEAL_END - REVEAL_START)
    )
    const intervalPx = (REVEAL_END - REVEAL_START) * totalRef.current
    const risePx = RISE_RATIO * intervalPx
    const offsetPx = (1 - rl) * risePx // how far below final the block sits
    const x = clamp((window.innerHeight / 2 - offsetPx) / FADE_BAND)
    const op = x * x * (3 - 2 * x) // smoothstep — fades in low in the viewport
    stage.style.setProperty("--sharp", `${1 - t}`)
    stage.style.setProperty("--reveal", `${rl}`) // LINEAR slide driver
    stage.style.setProperty("--op", `${op}`)
    stage.style.setProperty("--rise-left", `${risePx}px`)
    stage.style.setProperty("--rise-panel", `${risePx}px`)
  }, [])

  const applyFromScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const total = track.offsetHeight - window.innerHeight
    totalRef.current = total
    const p =
      total > 0
        ? Math.min(1, Math.max(0, -track.getBoundingClientRect().top / total))
        : 0
    applyProgress(p)
  }, [applyProgress])

  // scroll-driven blur/reveal (rAF-throttled). Reduced motion → final state, no listener.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const track = trackRef.current
      if (track) totalRef.current = track.offsetHeight - window.innerHeight
      applyProgress(1)
      return
    }
    let raf = 0
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0
          applyFromScroll()
        })
    }
    applyFromScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [applyFromScroll, applyProgress])

  // start the auto-advance once the stage is in view
  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true)
          io.disconnect()
        }
      },
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!started || !autoplay) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const id = setTimeout(
      () => setActive((a) => (a + 1) % insights.length),
      ADVANCE_MS
    )
    return () => clearTimeout(id)
  }, [started, active, autoplay, insights.length])

  // Clamped so a future reorder that stops putting `structure` last can't
  // render an undefined insight on the first paint after filtering.
  const activeIndex = Math.min(active, insights.length - 1)

  return (
    <div
      ref={trackRef}
      className="relative w-screen"
      style={{
        height: `${TRACK_VH}vh`,
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
      }}
    >
      <div
        ref={stageRef}
        className="sticky top-0 h-screen w-full overflow-hidden"
      >
        {/* background — static over-scale so blurred edges don't show; a static
            pre-blurred base + a sharp layer whose opacity cross-fades out. */}
        <div className="absolute inset-0" style={{ transform: "scale(1.05)" }}>
          <div
            className="absolute inset-0"
            style={{ filter: `blur(${MAX_BLUR}px) brightness(${1 - DARKEN})` }}
          >
            <Image
              src={BG_IMAGE}
              alt=""
              fill
              sizes="100vw"
              className="object-cover object-center"
            />
          </div>
          <div
            className="absolute inset-0"
            style={{ opacity: "var(--sharp, 1)", willChange: "opacity" }}
          >
            <Image
              src={BG_IMAGE}
              alt=""
              fill
              priority
              unoptimized
              sizes="100vw"
              className="object-cover object-center"
            />
          </div>
        </div>
        {/* legibility scrim */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.44) 42%, rgba(0,0,0,0.14) 74%), linear-gradient(0deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 42%)",
          }}
        />

        {/* content — header slides up out of view; the readout settles centre */}
        <div className="relative h-full">
          {/* header layer — left-aligned at rest, slides up on scroll. The
              `w-full` is load-bearing: without it the mx-auto SECTION shrinks to
              content width and the flex row centres it (heading drifts middle). */}
          <div className="pointer-events-none absolute inset-0 flex items-center">
            <div className={`${SECTION} w-full`}>
              <div
                className="flex max-w-[573px] flex-col gap-[30px]"
                style={{
                  transform: "translateY(calc(var(--reveal, 0) * -60vh))",
                }}
              >
                <h2 className="type-display-lg text-[#f6f6f6]">
                  {t(k.femaleHormonesRevealHeading)}
                </h2>
                <p className="type-title-lg max-w-[468px] font-medium text-white">
                  {t(k.femaleHormonesRevealSubhead)}
                </p>
              </div>
            </div>
          </div>

          {/* readout layer — centred; insights + card slide up from below + fade in */}
          <div className="absolute inset-0 flex items-center">
            <div className={`${SECTION} w-full`}>
              <div className="flex flex-col gap-12 lg:flex-row lg:items-center lg:gap-16 xl:gap-[130px]">
                {/* left — insight list (titles only) + CTA */}
                <div
                  className="flex flex-col gap-10 lg:min-w-0 lg:flex-[505_1_0%]"
                  style={{
                    opacity: "var(--op, 0)",
                    transform:
                      "translateY(calc((1 - var(--reveal, 0)) * var(--rise-left, 72px)))",
                  }}
                >
                  {insights.map((it, i) => {
                    const on = i === activeIndex
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setActive(i)}
                        aria-pressed={on}
                        className="relative cursor-pointer pl-[19px] text-left"
                      >
                        <span
                          aria-hidden
                          className={`absolute top-0 left-0 h-full w-[3px] rounded-full transition-[background-color,opacity] duration-300 ${
                            on
                              ? "bg-[#c14b33] opacity-100"
                              : "bg-[#fdf7f2] opacity-20"
                          }`}
                        />
                        {on && started && autoplay && (
                          <span
                            key={activeIndex}
                            aria-hidden
                            className="absolute top-0 left-0 w-[3px] rounded-full bg-[#e96f3f]"
                            style={{
                              animation: `hrFill ${ADVANCE_MS}ms linear forwards`,
                            }}
                          />
                        )}
                        <h3
                          className={`type-headline-sm transition-colors duration-300 ${
                            on ? "text-white" : "text-white/80 hover:text-white"
                          }`}
                        >
                          {t(it.titleKey)}
                        </h3>
                      </button>
                    )
                  })}
                  {/* CTA — fills the space freed under the tabs */}
                  <div className="pt-2">
                    <BookingCta
                      productId={[
                        PRODUCT_IDS.bloodTest,
                        PRODUCT_IDS.hormoneWomen,
                      ]}
                      step="addons"
                      trackingLabel="female_hormones_reveal_cta"
                      trackingLocation="go/female-hormones"
                    >
                      <Button
                        size="lg"
                        variant="primary"
                        className="group"
                        iconRight={
                          <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                        }
                      >
                        {t(k.femaleHormonesHeroCta1)}
                      </Button>
                    </BookingCta>
                  </div>
                </div>

                {/* right — white readout card */}
                <div
                  className="relative w-full rounded-[32px] bg-white p-8 lg:min-w-0 lg:flex-[672_1_0%] lg:p-12"
                  style={{
                    opacity: "var(--op, 0)",
                    transform:
                      "translateY(calc((1 - var(--reveal, 0)) * var(--rise-panel, 44px)))",
                  }}
                >
                  <Readout ins={insights[activeIndex]} />
                  {/* play / pause autoplay — top-right of the card, matching the
                      MRI/metabolism immersive canon (icon only, no chip). */}
                  <button
                    type="button"
                    onClick={() => setAutoplay((a) => !a)}
                    aria-label={
                      autoplay
                        ? t(k.femaleHormonesRevealPause)
                        : t(k.femaleHormonesRevealPlay)
                    }
                    className="absolute top-6 right-6 cursor-pointer text-neutral-400/70 transition-colors hover:text-neutral-600"
                  >
                    {autoplay ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <rect x="6" y="4" width="4" height="16" rx="1" />
                        <rect x="14" y="4" width="4" height="16" rx="1" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M7 4.5v15l12-7.5z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`@keyframes hrFill { from { height: 0 } to { height: 100% } }`}</style>
      </div>
    </div>
  )
}
